import React from "react";
import { createTheme } from "@mui/material/styles";
import { TickCircle, CloseCircle, Warning2, InfoCircle, Add } from "iconsax-react";
import {
  APP_TOAST_BACKGROUND,
  APP_TOAST_TEXT_COLOR,
  APP_TOAST_FONT_SIZE,
  APP_TOAST_FONT_FAMILY,
  APP_TOAST_MAX_WIDTH,
  APP_TOAST_TOP_OFFSET,
  APP_TOAST_ICON_TINTS,
  APP_SNACKBAR_ANCHOR_ORIGIN,
} from "./appToast";

// New toast look (Rahim, 2026-08-31, Fluent-style reference): one solid
// iconsax glyph, no wrapper — the "Bold" variant of these icons is already a
// single path (filled circle/hexagon with the check/x/! carved out as a
// transparent cutout, confirmed by reading iconsax-react's own source), so
// setting fill=color alone already renders "solid color shape, white mark
// showing through from the card behind it". A manually wrapped colored
// <span> behind it (the first attempt) double-drew the circle and read as
// two stacked icons (Rahim, 2026-08-31: "i only want one green tick icon
// with white tick inside why cant you get it from anywhere" — this WAS
// already sitting in the library, no lookup needed, just stop wrapping it).
const toastIcon = (Icon, color) =>
  React.createElement(Icon, { size: 22, variant: "Bold", color });

// iconsax has no bare "X" glyph (only CloseCircle/CloseSquare, both wrap it
// in a shape) — same rotated-Add trick as everywhere else a plain cross is
// needed. Wired as MuiAlert's default close icon via the `slots` API (MUI
// v6), so every Snackbar/Alert's built-in dismiss button picks it up without
// touching the 27 files that just pass `onClose`.
const ToastCloseIcon = (props) =>
  React.createElement(Add, {
    size: 26,
    variant: "Linear",
    // Fixed grey, not "currentColor" — the Alert's own text color is near-
    // black (APP_TOAST_TEXT_COLOR), which made the close X read as too heavy
    // (Rahim, 2026-08-31: "increase size of cross and make it grey").
    color: "rgba(0, 0, 0, 0.45)",
    style: { transform: "rotate(45deg)" },
    ...props,
  });

/** MuiSnackbar + MuiAlert defaults shared by light and dark theme */
const toastFeedbackComponents = {
  MuiSnackbar: {
    defaultProps: {
      anchorOrigin: APP_SNACKBAR_ANCHOR_ORIGIN,
    },
    styleOverrides: {
      // Must always render below the top nav, same offset the react-hot-toast
      // Toaster uses (App.js) — was un-offset before, sat right under the
      // viewport edge and could overlap the top nav / get clipped by it.
      root: {
        "&.MuiSnackbar-anchorOriginTopRight": {
          top: `${APP_TOAST_TOP_OFFSET}px !important`,
        },
      },
    },
  },
  MuiAlert: {
    defaultProps: {
      iconMapping: {
        success: toastIcon(TickCircle, APP_TOAST_ICON_TINTS.success),
        error: toastIcon(CloseCircle, APP_TOAST_ICON_TINTS.error),
        warning: toastIcon(Warning2, APP_TOAST_ICON_TINTS.warning),
        info: toastIcon(InfoCircle, APP_TOAST_ICON_TINTS.info),
      },
      slots: { closeIcon: ToastCloseIcon },
    },
    styleOverrides: {
      root: {
        backgroundColor: APP_TOAST_BACKGROUND,
        color: APP_TOAST_TEXT_COLOR,
        fontFamily: APP_TOAST_FONT_FAMILY,
        fontSize: APP_TOAST_FONT_SIZE,
        lineHeight: 1.45,
        fontWeight: 600,
        alignItems: "center",
        borderRadius: 12,
        border: "none",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
        // Same generous width as the react-hot-toast side (Rahim,
        // 2026-08-31: "i asked you to make the width more") — a floor, not a
        // fixed width, so a short message still shrink-wraps.
        minWidth: 360,
        maxWidth: APP_TOAST_MAX_WIDTH,
        "& .MuiAlert-icon": {
          padding: 0,
          marginRight: 12,
          opacity: 1,
        },
        "& .MuiAlert-message": {
          fontSize: "inherit",
          width: "100%",
        },
        "& .MuiTypography-root": {
          fontSize: "inherit",
          lineHeight: "inherit",
        },
      },
    },
  },
};

// Centralizes conventions that were being hand-copied per-button/per-dropdown
// instead of defined once (Rahim, 2026-08-31, after the "88 hover overrides
// in Inbox alone" audit: "do the 88+ mui call issue we talked abt"). Values
// are our OWN already-established numbers, not invented — Button matches the
// "Create & Chat"/Cancel convention independently repeated across InboxAll.jsx
// etc.; Menu/Popover/MenuItem reuse utils/dropdownMenuStyles.js's
// DROPDOWN_PAPER_SX/DROPDOWN_ITEM_SX (itself already extracted once before,
// after this exact object drifted between 3 copy-pasted files — same class of
// bug, theme-level this time). New buttons/dropdowns get these for free;
// existing inline sx isn't removed by this (that's a separate, larger
// per-file cleanup, not done here) but is now redundant where it matches.
const sharedInteractiveComponents = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: "8px",
        textTransform: "none",
        fontWeight: 600,
        fontSize: "0.8125rem",
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&:hover": { backgroundColor: theme.palette.action.hover },
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.palette.mode === "dark" ? "0 8px 24px rgba(0,0,0,0.4)" : "0 8px 24px rgba(0,0,0,0.12)",
        paddingTop: 4,
        paddingBottom: 4,
      }),
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.palette.mode === "dark" ? "0 8px 24px rgba(0,0,0,0.4)" : "0 8px 24px rgba(0,0,0,0.12)",
      }),
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: "0.85rem",
        paddingTop: 6.8, // 0.85 * 8px spacing unit, matches DROPDOWN_ITEM_SX's py:0.85
        paddingBottom: 6.8,
        paddingLeft: 12,
        paddingRight: 12,
        marginBottom: 2,
        borderRadius: "8px",
        gap: 8,
      },
    },
  },
};

// Define common typography settings
const typography = {
  fontFamily: "'Instrument Sans', 'Figtree', 'Lato', 'Roboto', sans-serif",
  fontSize: 12, // Reduce base font size even more
  h1: {
    fontWeight: 700,
    fontFamily: "'Instrument Sans', sans-serif",
    fontSize: "1.75rem", // Further reduced
  },
  h2: {
    fontWeight: 600,
    fontFamily: "'Instrument Sans', sans-serif",
    fontSize: "1.5rem", // Further reduced
  },
  h3: {
    fontWeight: 500,
    fontFamily: "'Instrument Sans', sans-serif",
    fontSize: "1.25rem", // Further reduced
  },
  h4: {
    fontWeight: 500,
    fontFamily: "'Instrument Sans', sans-serif",
    fontSize: "1.125rem", // Further reduced
  },
  h5: {
    fontWeight: 500,
    fontFamily: "'Instrument Sans', sans-serif",
    fontSize: "1rem", // Further reduced
  },
  h6: {
    fontWeight: 500,
    fontFamily: "'Instrument Sans', sans-serif",
    fontSize: "0.875rem", // Further reduced
  },
  body1: {
    fontSize: "0.75rem", // Smaller body text
  },
  body2: {
    fontSize: "0.625rem", // Even smaller secondary text
  },
  button: {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.75rem", // Reduced button text size
  },
};

// Add a custom colors object to store shared colors
const customColors = {
  dark: {
    navigation: "#0A0A0A", // Near-black nav (LeftNav/TopNav), dark mode only
  },
};

// Create light theme
const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#0E9046",
      light: "#4FC389",
      dark: "#006430",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#424242",
      light: "#6d6d6d",
      dark: "#1b1b1b",
      contrastText: "#ffffff",
    },
    background: {
      default: "#f5f5f5",
      paper: "#ffffff",
    },
    text: {
      primary: "#333333",
      secondary: "#666666",
    },
  },
  typography,
  components: {
    ...toastFeedbackComponents,
    ...sharedInteractiveComponents,
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          color: "#333333",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#D9D9D91A",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          position: "sticky",
          top: 0,
          zIndex: 2,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.MuiTableRow-head": {
            backgroundColor: "#FFFFFF",
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          overflow: "auto",
          maxHeight: "100%",
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: "separate",
          borderSpacing: 0,
        },
      },
    },
  },
  customColors, // Add custom colors to the theme
});

// Create dark theme
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    // Same #0E9046 as light mode used to sit here too — reads dark/muddy against a
    // near-black background. Promoted the palette's own existing "light" shade to
    // `main` for dark mode instead (standard practice: dark themes want a lighter,
    // more vibrant accent than light themes do) — every primary.main usage across
    // the app (badges, active states, underlines, selected rows) picks this up
    // automatically, no per-component changes needed.
    primary: {
      main: "#4FC389",
      light: "#84D5AC",
      dark: "#0E9046",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#B0BEC5",
      light: "#E2F1F8",
      dark: "#808E95",
      contrastText: "#000000",
    },
    background: {
      default: "#121212",
      paper: "#1E1E1E",
    },
    text: {
      primary: "#E0E0E0",
      secondary: "#AAAAAA",
    },
  },
  typography,
  components: {
    ...toastFeedbackComponents,
    ...sharedInteractiveComponents,
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: customColors.dark.navigation,
          color: "#E0E0E0",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: customColors.dark.navigation,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: "#1E1E1E",
          position: "sticky",
          top: 0,
          zIndex: 2,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.MuiTableRow-head": {
            backgroundColor: "#1E1E1E",
          },
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
        },
        head: {
          color: "#E0E0E0",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#1E1E1E",
          backgroundImage: "none",
        },
      },
    },
  },
  customColors, // Add custom colors to the theme
});

// Export the light theme as default for now
// The app will handle toggling between light and dark themes
export default lightTheme;
export { lightTheme, darkTheme };
